import React from "react";
import {
    AlertProvider,
    getThemeByKey,
    LLProvider,
    AppContextValue,
    AppContext,
    ErrorBoundary,
    Frame,
    FrameContextConsumer,
    LocaleProvider
} from "@msidata/zeact";
import { PopupPropsContext } from "@progress/kendo-react-popup";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import { settings } from "../../framework/core/settings";
import { loadLanguage, makeAPIClient } from "../../app/core/util/util";
import useMedia from "use-media";
import _ from "lodash";
import { create } from "jss";
import { withStyles, jssPreset } from "@material-ui/core/styles";
import { StylesProvider } from "@material-ui/styles";
import { FixStylesWrapper } from "../../app/admin/notifications/fixStyles";
import { getUserLanguageString } from "../../app/core/core";

export interface ZeactWrapperProps {
    children: React.ReactNode;
    height?: string;
}

const config = _.cloneDeep(window.z.configuration);

if (!config.serviceProApiUrl) {
    config.serviceProApiUrl = window.location.origin;
}

const materialStyles = (theme: any) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        flexGrow: 1,
        height: 400,
        border: "none",
        boxShadow: theme.shadows[1]
    }
});

export const apiClient = makeAPIClient(config);

function getAllCssRules() {
    var rules = {};
    try {
        for (var i = 0; i < document.styleSheets.length; ++i) {
            var cssRules = (document.styleSheets[i] as any).cssRules;
            for (var j = 0; j < cssRules.length; ++j)
                rules[cssRules[j].cssText] = cssRules[j];
        }
    } catch (e) {
        return {};
    }
    return rules;
}

function getRulesForSelector(name) {
    var rules: any = getAllCssRules();
    const rule = _.find(rules, (value, _key) => value.selectorText === name);
    return rule?.cssText || "";
}

const InjectStyles: React.FC<{
    target: HTMLElement;
    inheritSelectors?: string[];
}> = ({ target, inheritSelectors = [] }) => {
    React.useEffect(() => {
        target.insertAdjacentHTML(
            "beforebegin",
            `<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/svg-with-js.min.css" integrity="sha512-W3ZfgmZ5g1rCPFiCbOb+tn7g7sQWOQCB1AkDqrBG1Yp3iDjY9KYFh/k1AWxrt85LX5BRazEAuv+5DV2YZwghag==" crossorigin="anonymous" referrerpolicy="no-referrer">
             <link rel="stylesheet" type="text/css" href="https://unpkg.com/@progress/kendo-theme-material@3.33.1/dist/all.css">
             <script src="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/js/all.js" integrity="sha512-ia48mJh6IXv6baAI07Ib/crgsG/MD4tbSnOndWLo3GSExMhYsn1xPbni40VamCaXWk79t1dxi/JahsqOFazkew==" crossorigin="anonymous" referrerpolicy="no-referrer"></script>
             <style>
                body * {
                    font-family: BlinkMacSystemFont, Segoe UI, Trebuchet MS, Tahoma, Verdana, Arial, sans-serif;
                }
                .hidden {
                    display: none !important;
                    visibility: hidden !important;
                }
                ${inheritSelectors.map(s => getRulesForSelector(s)).join("\n")}
             </style>`
        );
    }, [inheritSelectors, target]);
    return null;
};

export const ZeactWrapper = (props: ZeactWrapperProps) => {
    const themeKey = settings.brandName || "servicepro";
    const theme = getThemeByKey(themeKey);

    const isMobile = useMedia({ maxWidth: 650 });

    const appContextValue = {
        config: config,
        apiClient,
        isMobile
    };

    const languageId = getUserLanguageString();
    const localeId = localeIdFromLanguageId(languageId);
    loadLanguage(languageId);

    return (
        <ThemeProvider theme={theme}>
            <AppContext.Provider value={appContextValue as AppContextValue}>
                <LocaleProvider
                    value={{
                        localeId
                    }}
                >
                    <LLProvider value={{ languageId }}>
                        <AlertProvider maxAlerts={5}>
                            <ErrorBoundary>{props.children}</ErrorBoundary>
                        </AlertProvider>
                    </LLProvider>
                </LocaleProvider>
            </AppContext.Provider>
        </ThemeProvider>
    );
};

function localeIdFromLanguageId(languageId: string) {
    return `${languageId.split("-")[0]}-${languageId
        .split("-")[1]
        .toUpperCase()}`;
}
export const ZeactInjector = withStyles(materialStyles, { withTheme: true })(
    (props: ZeactWrapperProps) => {
        const themeKey = settings.brandName || "servicepro";
        const theme = getThemeByKey(themeKey);

        const isMobile = useMedia({ maxWidth: 650 });

        const appContextValue = {
            config: config,
            apiClient,
            isMobile
        };

        const languageId = getUserLanguageString();
        const localeId = localeIdFromLanguageId(languageId);
        loadLanguage(languageId);
        const sm = React.useRef(new Map());
        return (
            <Frame
                style={{
                    width: "98vw",
                    height: props.height || "104vh",
                    border: "none",
                    boxShadow: "none"
                }}
            >
                <FrameContextConsumer>
                    {frameContext => (
                        <StyleSheetManager target={frameContext.document.head}>
                            <React.Fragment>
                                <InjectStyles
                                    target={frameContext.document.head}
                                />
                                <ThemeProvider theme={theme}>
                                    <StylesProvider
                                        jss={create({
                                            plugins: [...jssPreset().plugins],
                                            insertionPoint:
                                                frameContext.document.head
                                        })}
                                        sheetsManager={sm.current}
                                    >
                                        <AppContext.Provider
                                            value={
                                                appContextValue as AppContextValue
                                            }
                                        >
                                            <FixStylesWrapper>
                                                <div id="zeact">
                                                    <LocaleProvider
                                                        value={{
                                                            localeId
                                                        }}
                                                    >
                                                        <LLProvider
                                                            value={{
                                                                languageId
                                                            }}
                                                        >
                                                            <AlertProvider
                                                                maxAlerts={5}
                                                            >
                                                                <ErrorBoundary>
                                                                    <PopupPropsContext.Provider
                                                                        value={p => ({
                                                                            ...p,
                                                                            appendTo:
                                                                                frameContext.document.getElementById(
                                                                                    "zeact"
                                                                                )
                                                                        })}
                                                                    >
                                                                        {
                                                                            props.children
                                                                        }
                                                                    </PopupPropsContext.Provider>
                                                                </ErrorBoundary>
                                                            </AlertProvider>
                                                        </LLProvider>
                                                    </LocaleProvider>
                                                </div>
                                            </FixStylesWrapper>
                                        </AppContext.Provider>
                                    </StylesProvider>
                                </ThemeProvider>
                            </React.Fragment>
                        </StyleSheetManager>
                    )}
                </FrameContextConsumer>
            </Frame>
        );
    }
);
